import React, {useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {Typography} from '@mui/material'
import {format, parseISO} from 'date-fns'
import { localeDateFormat } from '../../utils/localeUtils'
import Card from '../../shared/layout/Card'
import PageHeader from '../../shared/layout/PageHeader/PageHeader'
import EnhancementsReportTab from './EnhancementsReportTab'
import {useEnhancementsReport} from './hooks/useEnhancementsReport'
import {useEnhancementsFilter} from './hooks/useEnhancementsFilter'
import {RootStore} from '../../redux/store'
import {hasUserAccessToRoute} from '../../utils/app-utils'

const formatDataLastUpdated = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Data refresh pending'

  try {
    const date = parseISO(dateString)
    const formattedTime = format(date, `${localeDateFormat} HH:mm`)
    return `Data last updated: ${formattedTime}`
  } catch (error) {
    console.warn('Failed to format data last updated:', error)
    return 'Data refresh pending'
  }
}

function DataLastUpdatedDisplay({dataLastUpdated}: { dataLastUpdated: string | null | undefined }) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{fontStyle: 'italic'}}
    >
      {formatDataLastUpdated(dataLastUpdated)}
    </Typography>
  )
}

function EnhancementsReport() {
  const navigate = useNavigate()

  const isContractor = useSelector<RootStore, boolean>(
    (state: RootStore) => state.appSettings.employeeDetails.isContractor
  )
  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )
  const {userSettings} = useSelector((state: RootStore) => state.appSettings)
  const enhancementsReportHook = useEnhancementsReport()
  const {
    dataLastUpdated,
    handleFilterChange,
    handleSearch,
    resetForm
  } = enhancementsReportHook
  const filterHook = useEnhancementsFilter({
    resetForm,
    onFilterChange: handleFilterChange,
    onSearch: handleSearch,
  })

  useEffect(() => {
    handleFilterChange(filterHook.currentSearch)
  }, [filterHook.currentSearch, handleFilterChange])

  useEffect(() => {
    if (!userPermissions || !userSettings) {
      return
    }

    if (
      !hasUserAccessToRoute('/reports/enhancements', userPermissions, isContractor, userSettings)
    ) {
      navigate('/dashboard')
    }
  }, [isContractor, userSettings, navigate, userPermissions])

  if (!userSettings || !userPermissions) {
    return null
  }

  return (
    <>
      <PageHeader title="Reporting" />
      <Card title="Enhancements" icon={<DataLastUpdatedDisplay dataLastUpdated={dataLastUpdated} />}>
        <EnhancementsReportTab
          enhancementsReportHook={enhancementsReportHook}
          {...filterHook}
        />
      </Card>
    </>
  )
}

export default EnhancementsReport